import React from "react";
import { useNavigation } from "@react-navigation/native";
import { useFertilityTreatments } from "../FertilityTreatmentsContext";
import { transformFertilityTreatmentsToOperations } from "../transformFertilityTreatmentsState";
import {
  useDeleteCycle,
  usePatient,
  type UpdatePatientInput,
} from "@louise/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import {
  CanonicalCycleSaveError,
  useCanonicalCycleIntake,
} from "@src/packages/care/clinical-record";
import {
  BaseCompleteScreen,
  navigateToNextSection,
  getNextSectionButtonLabel,
} from "@louise/medical-history/components/BaseCompleteScreen";
import { useTranslations } from "@src/packages/i18n";
import { createIdempotencyKey } from "@src/packages/care/cryovault/useSpecimens";
import { useSaveFETHistoricalRecord } from "../useFETHistoricalRecords";

type PriorFertilityTreatmentPayload = UpdatePatientInput & {
  fertilityHistory: {
    id?: string;
    hasPriorFertilityTreatments: boolean;
  };
};

export function FertilityTreatmentsCompleteScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTreatments();
  const { mutateAsync: deleteCycle, isPending: isDeleting } = useDeleteCycle();
  const { save: saveCanonicalCycle, isPending: isSavingCanonicalCycle } =
    useCanonicalCycleIntake();
  const { mutateAsync: updatePatient, isPending: isUpdatingPatient } =
    useQueuedPatientUpdate();
  const { mutateAsync: saveFETHistoricalRecord, isPending: isSavingFET } =
    useSaveFETHistoricalRecord();
  const { data: patient } = usePatient();
  const navigation = useNavigation();
  const [saveError, setSaveError] = React.useState(false);

  const isSubmitting =
    isSavingCanonicalCycle || isDeleting || isUpdatingPatient || isSavingFET;

  const savePriorTreatmentAnswer = async (answer: boolean) => {
    const payload: PriorFertilityTreatmentPayload = {
      fertilityHistory: {
        id: patient?.fertilityHistory?.id,
        hasPriorFertilityTreatments: answer,
      },
    };
    await updatePatient(payload);
  };

  const handleFinish = async () => {
    setSaveError(false);
    try {
      // Transform state to cycle operations
      const cycleOperations = transformFertilityTreatmentsToOperations(state);

      if (cycleOperations.length === 0) {
        if (state.hasTreatmentsDone === false) {
          await savePriorTreatmentAnswer(false);
        }
        navigateToNextSection(navigation, "fertilityTreatments", patient);
        return;
      }

      await savePriorTreatmentAnswer(state.hasTreatmentsDone === true);

      let persistedTreatments = state.treatments;

      // Persist every entry through the same cycle/cohort boundary. A cycle
      // is committed before its optional oocyte cohort so a lost cohort
      // response can be retried without creating another cycle.
      for (const op of cycleOperations) {
        if (op.type === "delete") {
          await deleteCycle(op.id);
          continue;
        }

        try {
          const saved = await saveCanonicalCycle({
            cycleId: op.type === "update" ? op.cycleId : undefined,
            cycleType: op.data.cycleType ?? null,
            status: op.data.status,
            description: op.data.description,
            protocol: op.data.protocol,
            menstruationStartDate: op.data.menstruationStartDate,
            startDate: op.data.startDate,
            endDate: op.data.endDate,
            rawCycleType: op.data.rawCycleType,
            rawProtocol: op.data.rawProtocol,
            provider: op.data.provider,
            clinic: op.data.clinic,
            phases: op.data.phases,
            outcome: op.data.outcome,
            dayNotes: op.data.dayNotes,
          });
          if (op.type === "create") {
            persistedTreatments = persistedTreatments.map((treatment, index) =>
              index === op.treatmentIndex
                ? { ...treatment, id: saved.cycle.id }
                : treatment,
            );
            updateState({ treatments: persistedTreatments });
          }

          if (op.fetHistory) {
            const { historyRecordId, ...historyInput } = op.fetHistory;
            await saveFETHistoricalRecord({
              ...historyInput,
              cycleId: saved.cycle.id,
              clientRequestId:
                historyInput.clientRequestId ?? createIdempotencyKey(),
              ...(historyRecordId
                ? { supersedesRecordId: historyRecordId }
                : {}),
            });
          }
        } catch (error) {
          // If the cycle committed but its cohort failed, retain the cycle ID
          // before surfacing the error so retry updates the same graph.
          if (
            op.type === "create" &&
            error instanceof CanonicalCycleSaveError &&
            error.cycleId
          ) {
            persistedTreatments = persistedTreatments.map((treatment, index) =>
              index === op.treatmentIndex
                ? { ...treatment, id: error.cycleId! }
                : treatment,
            );
            updateState({ treatments: persistedTreatments });
          }
          throw error;
        }
      }

      navigateToNextSection(navigation, "fertilityTreatments", patient);
    } catch {
      setSaveError(true);
    }
  };

  return (
    <BaseCompleteScreen
      subtitle={t("med_history.fertility.treatments.complete_subtitle")}
      secondarySubtitle={
        saveError ? t("med_history.fertility.treatments.save_error") : null
      }
      buttonLabel={getNextSectionButtonLabel(t, "fertilityTreatments", patient)}
      isSubmitting={isSubmitting}
      onFinish={handleFinish}
    />
  );
}

export default FertilityTreatmentsCompleteScreen;
